import React from 'react';
import styled from 'styled-components/macro';
import { useHistory } from 'react-router';
import queryString from 'query-string';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import {
  CustomReportModal,
  DateRangeSelector,
  SendReportModal,
  RecurringModal,
  timeRangeConstants,
  UsersDropdown,
  calculateDatesForRange,
} from '../../components';
import { MdFileDownload, MdEmail } from 'react-icons/md';
import { LoadingIndicator, Table } from 'lib/components';
import { reportData, reportTypes } from 'lib/const';
import { downloadReportData } from 'lib/api';
import { Field } from 'lib/components/modal';
import { addThousandCommaSeparator } from 'lib/utils/functions';
import { useAuth } from 'lib/context';
import { CompanyDropdown } from '../../components/CompanyDropdown';
import { VIDEO_ATTRIBUTES } from 'lib/utils/videoAttributes';
import { Container, Gap, MainWrapper } from 'lib/components/styles/layout';
import { Heading } from 'lib/components/styles/typography';
import { NotFound } from 'app/pages/notFound/NotFound';
import { STANDARD_DATE_FORMAT } from 'lib/const/DateFormat';
import { getDateRangeFromLocalstorage } from '../../components/DateRangeSelector/DateRangeSelector';
import { useGetAllReportsQuery } from 'lib/api/metrics/queryAllReports';
import { Button } from 'react-covideo-common';
import { BiCalendarEvent } from 'react-icons/bi';

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 32px;
`;

const TableCell = styled.div<{ clicked?: boolean }>`
  /**** SUS-926 changes ****/
  width: 200px;
  padding-left: 24px;
  cursor: ${props =>
    !!props.clicked ? 'pointer' : 'auto'}; /**** SUS-926 changes ****/
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`;

const tableFields = [
  {
    value: 'label',
    label: 'Data Type',
  },
  {
    value: 'count',
    label: 'Value',
  },
];

const API_DATE_FORMAT = 'YYYY-MM-DD';

const QUERY_DATE_FORMAT = STANDARD_DATE_FORMAT;

export const Main = () => {
  const {
    userData: { userId, customerId, isResellerAdmin, isCompanyAdmin },
  } = useAuth();
  const location = useLocation();
  const queryParams: any = queryString.parse(location.search);
  const [showNewReportModal, setShowNewReportModal] = React.useState(false);
  const [showSendReportModal, setShowSendReportModal] = React.useState('');
  const storedDateRange = getDateRangeFromLocalstorage();
  const initRange =
    queryParams.range ||
    storedDateRange?.range ||
    timeRangeConstants.LAST_7_DAYS;
  const { start, end } = calculateDatesForRange(
    initRange,
    storedDateRange?.start,
    storedDateRange?.end
  );
  const [startDate, setStartDate] = React.useState<Date>(start);
  const [endDate, setEndDate] = React.useState<Date>(end);
  const [dateRange, setDateRange] = React.useState(initRange);

  const [initialReports, setInitialReports] = React.useState<Field[]>([]);
  const [customReportId, setCustomReportId] = React.useState('');
  const [showRecurringReportModal, setShowRecurringReportModal] =
    React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(userId);
  const [selectedCustomer, setSelectedCustomer] = React.useState(customerId);

  const history = useHistory();

  queryParams.start = dayjs(startDate).format(QUERY_DATE_FORMAT);
  queryParams.end = dayjs(endDate).format(QUERY_DATE_FORMAT);
  queryParams.range = dateRange;
  queryParams.user = selectedUser || userId;

  const users = isCompanyAdmin ? [selectedUser] : [];
  const customer = isResellerAdmin ? selectedCustomer : '';

  const params = {
    from: dayjs(startDate).format(API_DATE_FORMAT),
    to: dayjs(endDate).format(API_DATE_FORMAT),
    users: users.join(','),
    customer,
  };

  const { data, isLoading: loading } = useGetAllReportsQuery(params);

  const downloadReport = (report: any) => {
    if (report.value === reportTypes.VIDEO_ATTRIBUTES) {
      return downloadReportData(
        `${report.url}/download`,
        `${report.value}.csv`,
        startDate,
        endDate,
        [],
        '',
        'POST',
        {
          user: selectedUser,
          downloadType: 'DETAILS',
        }
      );
    }
    return downloadReportData(
      `${report.url}/download`,
      `${report.value}.csv`,
      startDate,
      endDate,
      users
    );
  };

  React.useEffect(() => {
    if (!customerId) {
      return;
    }
    setSelectedCustomer(customerId);
  }, [customerId]);

  const reports = (data || []).map((d: any) => {
    let statisticsData: any = [];
    if (d) {
      // engagement rate is returned in different format { data, engagement
      statisticsData = d.data && d.data.data ? [...d.data.data] : [...d.data];
    }

    let count = 0;
    statisticsData.forEach((item: any) => {
      let currentCount = parseInt(item.cnt, 10) || 0;
      if (d.report === reportTypes.AVERAGE_VIDEO_DURATION) {
        currentCount = parseInt(item.duration, 10) || 0;
      }
      count += currentCount;
    });

    if (d.report === reportTypes.ENGAGEMENT) {
      count = (d.data && d.data.engagement) || 0;
    }

    if (
      d.report === reportTypes.AVERAGE_VIDEO_DURATION &&
      !!statisticsData.length
    ) {
      count = count / statisticsData.length;
    }

    return {
      ...d,
      ...reportData[d.report],
      count,
      value: d.report,
    };
  });

  if (!loading && !data) {
    return <NotFound />;
  }

  return (
    <Container>
      <MainWrapper resetPadding={false}>
        <Gap>
          <Heading>All Data</Heading>
          <DateRangeSelector
            onDateRangeChange={(start, end, range) => {
              setStartDate(start);
              setEndDate(end);
              setDateRange(range);
            }}
            disabled={loading}
            initialValue={dateRange}
          />
          {isResellerAdmin && (
            <CompanyDropdown
              onCustomerSelected={c => setSelectedCustomer(c)}
              initialCustomer={selectedCustomer}
              disabled={loading}
              width='200px' /* SUS-1136 changes */
            />
          )}
          {isCompanyAdmin && selectedCustomer !== 'all' && (
            <UsersDropdown
              onUserSelected={user => setSelectedUser(user)}
              initialUser={userId}
              customer={selectedCustomer}
              disabled={loading}
              width='200px'
            />
          )}
        </Gap>

        {loading && <LoadingIndicator isLoading={loading} height='300px' />}
        {!loading && (
          <TableWrapper>
            <Table
              compact={true}
              headers={[
                ...tableFields.map(item => {
                  return <TableCell>{item.label}</TableCell>;
                }),
                <TableCell />,
              ]}
              hoverable={false}
              rows={reports.map((report: any, index: number) => ({
                key: index,
                columns: [
                  ...tableFields.map(item => {
                    const isEngagementRate =
                      report.value === reportTypes.ENGAGEMENT;
                    return (
                      <TableCell
                        clicked={true} /*** SUS-926 changes ***/
                        onClick={() => {
                          history.push(
                            `/reports/overview/${
                              report.value
                            }?${queryString.stringify(queryParams)}`
                          );
                        }}
                      >
                        {addThousandCommaSeparator(
                          report[item.value] || 0,
                          isEngagementRate
                        )}
                        {isEngagementRate && item.value === 'count' && ' %'}
                      </TableCell>
                    );
                  }),
                  <TableCell
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'end',
                      width: '100%',
                      paddingLeft: 0,
                      gap: '8px',
                    }}
                  >
                    <Button
                      icon={<MdEmail size={20} />}
                      onClick={() => setShowSendReportModal(report.url)}
                    />
                    <Button
                      icon={<MdFileDownload size={20} />}
                      onClick={() => downloadReport(report)}
                    />
                    <Button
                      text='Create Recurring'
                      icon={<BiCalendarEvent size={20} />}
                      onClick={() => {
                        setInitialReports([
                          {
                            label: report.label,
                            value: report.value.includes(VIDEO_ATTRIBUTES)
                              ? `${report.value}~~details`
                              : report.value,
                          },
                        ]);
                        setShowNewReportModal(true);
                      }}
                    />
                  </TableCell>,
                ],
                onClick: () => {},
              }))}
            />
          </TableWrapper>
        )}
        {showNewReportModal && (
          <CustomReportModal
            reports={initialReports}
            goToReportDetails={false}
            users={
              initialReports.length &&
              initialReports[0].value.includes(VIDEO_ATTRIBUTES)
                ? [selectedUser || userId]
                : users
            } /*** SUS-853 changes ***/
            handleModalClose={reportId => {
              setShowNewReportModal(false);
              if (reportId) {
                setCustomReportId(reportId);
                setShowRecurringReportModal(true);
              }
            }}
          />
        )}

        {showSendReportModal && (
          <SendReportModal
            handleModalClose={() => setShowSendReportModal('')}
            startDate={startDate}
            endDate={endDate}
            reportUrl={showSendReportModal}
            range={dateRange}
            users={users}
            extraParams={
              showSendReportModal.includes(reportTypes.VIDEO_ATTRIBUTES)
                ? { user: selectedUser || userId, downloadType: 'DETAILS' }
                : {}
            }
            skipCreateRecurring={true}
          />
        )}

        {showRecurringReportModal && customReportId && (
          <RecurringModal
            reportId={customReportId}
            handleModalClose={recurringId => {
              setShowRecurringReportModal(false);
              if (recurringId) {
                history.push(`/reports/custom/${customReportId}`);
              }
            }}
          />
        )}
      </MainWrapper>
    </Container>
  );
};
